import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from 'react-router-dom';
import {Anchor, Breadcrumbs, Checkbox, Textarea, TextInput, Select} from "@mantine/core";
import S from './ProductPage.module.scss';
import {projectsInfoPage} from "../../shared/mock/projects";
import {Carousel} from "@mantine/carousel";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Lightbox from "yet-another-react-lightbox";
import {useForm} from "@mantine/form";
import {notifications} from "@mantine/notifications";
import {PatternFormat} from "react-number-format";
import {useElementOnScreen} from "../../shared/hook/useElementOnScreen";

interface FormValues {
    name: string;
    company: string;
    phone: string;
    email: string;
    message?: string;
    product: string;
    title: string;
    check: boolean;
}

export const ProductPage = () => {
    const { titleProduct } = useParams();
    const navigate = useNavigate();

    const [index, setIndex] = useState(-1);
    const [formRef, isVisible] = useElementOnScreen<HTMLElement>();

    const items = [
        { title: 'Продукты и платформы', url: '/#projects' },
        { title: titleProduct, active: true },
    ].map((item, index) => (
        <Anchor
            onClick={() => item.url && navigate(item.url)}
            key={index}
            data-active={item.active || undefined}
            className={item.active ? S.breadcrumbItemActive : S.breadcrumbItem}
        >
            {item.title}
        </Anchor>
    ));

    const project = useMemo(() => {
        if (!titleProduct) return null;
        const decodedTitle = decodeURIComponent(titleProduct);
        return projectsInfoPage.find(
            (item) => item.id === decodedTitle
        );
    }, [titleProduct]);

    const slides = useMemo(
        () => project?.images?.map((src) => ({ src })) || [],
        [project?.images]
    );

    const [isLoading, setIsLoading] = useState(false);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const titleList = [
        {
          id: 1,
          name: 'Демо'
        },
        {
            id: 2,
            name: 'Презентация'
        },
    ];

    const titleOptions = useMemo(() => {
        return titleList.map((item) => ({
            value: item.name,
            label: item.name
        }));
    }, [titleList]);

    const validateForm = (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (!values.name) errors.name = 'обязательное поле';
        if (!values.product) errors.product = 'обязательное поле';
        if (!values.title) errors.title = 'обязательное поле';
        if (!values.email) {
            errors.email = 'обязательное поле';
        } else if (!emailRegex.test(values.email)) {
            errors.email = 'некорректный email';
        }
        if (!values.phone || values.phone.replace(/\D/g, '').length < 11) {
            errors.phone = 'заполните номер телефона';
        }
        if (!values.check) errors.check = 'необходимо согласие на обработку данных';

        return errors;
    };

    const productForm = useForm<FormValues>({
        initialValues: {
            product: titleProduct || '',
            name: '',
            company: '',
            email: '',
            phone: '',
            title: '',
            check: false,
        },
        validate: (values) => validateForm(values),
    });

    useEffect(() => {
        if (titleProduct) {
            productForm.setValues({
                product: titleProduct || '',
            });
        }
    }, [titleProduct]);

    const sendRequest = async (values: FormValues) => {
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('company', values.company || '');
        formData.append('phone', values.phone.replace(/\D/g, ''));
        formData.append('email', values.email);
        formData.append('message', values.message || '');
        formData.append('product', values.product || '');
        formData.append('request', values.title || '');

        const response = await fetch('/api/v1/request', {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Ошибка ${response.status}: ${errorText || response.statusText}`);
        }

        return {success: true};
    };

    const handleSubmit = productForm.onSubmit(async (values) => {
        setIsLoading(true);

        try {
            await sendRequest(values);
            notifications.show({
                color: 'green',
                autoClose: 5000,
                title: 'Заявка успешно отправлена!',
                message: '',
            })
            productForm.reset();
            if (titleProduct) {
                productForm.setValues({
                    product: titleProduct || '',
                });
            }

        } catch (error) {
            notifications.show({
                color: 'red',
                autoClose: 5000,
                title: 'Произошла ошибка при отправке. Пожалуйста, попробуйте позже.',
                message: '',
            })
        } finally {
            setIsLoading(false);
        }
    });

    if (!project) {
        return <div className={S.notFound}>Проект не найден</div>;
    }

    return (
        <div className={S.root}>
            <div className={S.elements}>
                <Breadcrumbs>
                    {items}
                </Breadcrumbs>
                <div className={S.content}>
                    <div className={S.divHeader}>
                        <div className={S.title}>{project.title}</div>
                        <div className={S.description}>{project?.description}</div>
                    </div>

                    {project?.subDescription || project?.tags ? (
                        <div className={S.projectDescriptionDiv}>
                            {project?.tags && <div className={S.projectDescription}>Возможности</div>}
                            <div className={S.subDescription}>{project?.subDescription}</div>
                            <div className={S.projectTagsDiv}>
                                {project?.tags?.map((item) => (
                                    <div className={S.cardDiv}>
                                        <div className={S.boxNumberDiv}>
                                            <div className={S.boxNumber}>{item?.number}</div>
                                            <div className={S.numberText}>{item?.text}</div>
                                        </div>
                                        <div className={S.subItemsText}>{item?.subItems}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {project.images && project.images.length > 0 && (
                        <Carousel
                            classNames={{controls: S.controls, indicator: S.indicator, root: S.carouselRoot}}
                            withIndicators
                            slideSize={{base: '100%', sm: '80%', md: '60%'}}
                            slideGap={{base: 0, sm: 'xs', md: 'xs'}}
                            emblaOptions={{loop: true, align: 'start'}}
                        >
                            {project.images?.map((image, index) => (
                                <Carousel.Slide
                                    key={index}
                                    onClick={() => setIndex(index)}
                                    className={S.carouselSlide}
                                >
                                    <img
                                        src={image}
                                        alt={`${project.title} screenshot ${index + 1}`}
                                        className={S.carouselImage}
                                    />
                                </Carousel.Slide>
                            ))}
                        </Carousel>
                    )}

                    <Lightbox
                        index={index}
                        open={index >= 0}
                        close={() => setIndex(-1)}
                        slides={slides}
                        plugins={[Thumbnails, Zoom]}
                        carousel={{
                            spacing: "4px",
                            padding: "4px",
                        }}
                        zoom={{
                            maxZoomPixelRatio: 3,
                            zoomInMultiplier: 2,
                            doubleClickDelay: 300,
                            doubleTapDelay: 300,
                            keyboardMoveDistance: 50,
                            wheelZoomDistanceFactor: 100,
                            pinchZoomDistanceFactor: 100,
                            scrollToZoom: true
                        }}
                    />

                    {project?.info && project.info.length > 0 ? (
                        <div className={S.projectDescriptionDiv}>
                            <div className={S.projectInfo}>Инструкции</div>
                            {project?.info?.map((item: any) => (
                                <div key={item?.id} className={S.infoFileDiv}>
                                    <a href={item?.file} target="_blank" rel="noopener noreferrer">
                                        • {item?.name}
                                    </a>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    <section className={project?.id === 'Bench' ? S.containerBench : S.container} ref={formRef}>
                        <div className={`${S.requestForm} ${isVisible ? S.visible : ''}`}>
                            <div className={S.leftBlock}>
                                <div className={S.title}>Оставь заявку</div>
                                <div className={S.description}>для запроса демо или презентации</div>
                            </div>
                            <div className={S.rightBlock}>
                                <form className={`${S.form} ${isVisible ? S.visible : ''}`} onSubmit={handleSubmit}>
                                    <div className={S.formRow}>
                                        <div className={S.formGroup}>
                                            <TextInput
                                                variant="filled"
                                                placeholder="продукт*"
                                                value={titleProduct}
                                            />
                                        </div>
                                        <div className={S.formGroup}>
                                            <Select
                                                variant="filled"
                                                placeholder="заявка*"
                                                data={titleOptions}
                                                key={productForm.key('title')}
                                                {...productForm.getInputProps('title')}
                                                searchable
                                                clearable
                                                styles={{
                                                    dropdown: {
                                                        background: '#e3e8ea',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                                        padding: '6px',
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={S.formRow}>
                                        <div className={S.formGroup}>
                                            <TextInput
                                                variant="filled"
                                                placeholder="имя, фамилия*"
                                                key={productForm.key('name')}
                                                {...productForm.getInputProps('name')}
                                            />
                                        </div>
                                        <div className={S.formGroup}>
                                            <TextInput
                                                variant="filled"
                                                placeholder="название организации"
                                                key={productForm.key('company')}
                                                {...productForm.getInputProps('company')}
                                            />
                                        </div>
                                    </div>
                                    <div className={S.formRow}>
                                        <div className={S.formGroup}>
                                            <PatternFormat
                                                format="+7 ### ### ## ##"
                                                mask="_"
                                                customInput={TextInput}
                                                variant="filled"
                                                placeholder="телефон*"
                                                value={productForm.values.phone}
                                                {...productForm.getInputProps('phone')}
                                            />
                                        </div>
                                        <div className={S.formGroup}>
                                            <TextInput
                                                variant="filled"
                                                placeholder="email*"
                                                key={productForm.key('email')}
                                                {...productForm.getInputProps('email')}
                                            />
                                        </div>
                                    </div>
                                    <div className={S.formGroup}>
                                        <Textarea
                                            placeholder="ваше сообщение"
                                            autosize
                                            minRows={2}
                                            maxRows={4}
                                            key={productForm.key('message')}
                                            {...productForm.getInputProps('message')}
                                        />
                                    </div>
                                    <div className={S.checkboxGroup}>
                                        <Checkbox
                                            className={S.checkboxLabel}
                                            label={
                                                <span className={S.checkboxText}>согласен на обработку <u
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open('/agreement', '_blank');
                                                    }}>персональных данных</u></span>
                                            }
                                            color="orange"
                                            key={productForm.key('check')}
                                            {...productForm.getInputProps('check', {type: 'checkbox'})}
                                        />
                                    </div>
                                    <button type="submit" className={S.submitButton}>
                                        {isLoading ? 'отправка...' : 'отправить'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};